import type { SxProps, Theme } from "@mui/material";
import { Box } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import type { LottieHandle } from "lottie-react";
import { Lottie } from "lottie-react";
import { type FC, useEffect, useRef, useState } from "react";
import { MOTION, UNLOCK_BURST } from "../../../theme/tokens";

export type UnlockBurstProps = {
	/** The trigger. Plays forward (an "arrival") on the false→true rising edge, in reverse (a
	 * "departure") on the true→false falling edge — exactly once per edge, never on mount. */
	active: boolean;
	/** The Lottie JSON to play. Callers own the asset (e.g. import a JSON under `assets/animations/`
	 * and pass it in) so this component stays generic across easter eggs. */
	animationData: object;
	/** Overlay width/height in px (square). Defaults to `UNLOCK_BURST.DEFAULT_SIZE`. */
	size?: number;
	dataTestId?: string;
	/** Positions the overlay relative to the nearest positioned ancestor. Defaults to popping up
	 * centered just above the anchor (`bottom: "100%"`, centered horizontally) — override for a
	 * different anchor point. */
	sx?: SxProps<Theme>;
};

// Mount fade-in (opacity only, `MOTION.DURATION.ENTRANCE_SM`) rather than popping in at full
// opacity. Restarts on every trigger because the whole overlay remounts via `key={playKey}` below.
// Reduced motion is already fully gated in JS (the overlay never mounts at all under it); the
// `prefers-reduced-motion` guard here just mirrors the house idiom (see `ChatCard`) as a second line
// of defense.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes unlockBurstFadeIn": {
		from: { opacity: 0 },
		to: { opacity: 1 },
	},
	animation: `unlockBurstFadeIn ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * Settle delay (ms) between unmounting the previous play and mounting the next one — roughly 1-2
 * frames at 60fps. A rapid re-toggle unmounts the in-flight instance synchronously (see the trigger
 * effect below), but the *new* instance's own mount/`onDOMLoaded` round-trip isn't instant; without
 * this gap a fast enough re-toggle could still overlap the previous instance's teardown with the
 * next one's startup and leave it seeking from a stale mid-animation frame instead of the correct
 * boundary (0 for a forward play, the last frame for a reverse one).
 */
const REPLAY_SETTLE_MS = 70;

/**
 * A small celebratory "unlock" burst — plays a Lottie animation once on EITHER edge of `active`
 * changing: forward from frame 0 on the false→true rising edge, and in reverse from the last frame
 * back to 0 on the true→false falling edge. That lets one asset read as both an "arrival" and a
 * "departure" moment (e.g. a rocket launching on true, landing on false) without a second asset or a
 * second component. Auto-dismisses via Lottie's `onComplete` (fires at either playback boundary).
 * Generic over any wordless gamification/easter-egg moment: the caller supplies `animationData`, this
 * owns only the edge-trigger + direction, auto-dismiss, mount fade-in, and reduced-motion gating.
 *
 * Renders as an absolutely-positioned, `pointerEvents: none`, `aria-hidden` overlay — it never
 * affects the parent's layout/size and never blocks interaction with the control it decorates.
 * The parent must be (or become) `position: relative` for the overlay to anchor correctly.
 */
export const UnlockBurst: FC<UnlockBurstProps> = ({ active, animationData, size = UNLOCK_BURST.DEFAULT_SIZE, dataTestId, sx }) => {
	const reducedMotion = useReducedMotion();
	// Seeded with the initial `active` value so the first effect run never reads as an edge — covers
	// both "starts false" and "starts true" (must not play on mount either way).
	const prevActiveRef = useRef(active);
	// Which edge triggered the MOST RECENTLY REQUESTED play: 1 = forward (false→true), -1 = reverse
	// (true→false). A ref (not state) so a rapid re-toggle during the settle delay just overwrites it —
	// only the latest request survives, never queues alongside an earlier one. Read once the
	// freshly-mounted Lottie instance is ready, in `handleDomLoaded` below.
	const directionRef = useRef<1 | -1>(1);
	const lottieRef = useRef<LottieHandle | null>(null);
	// The pending "mount the next play" timeout, if a settle delay is currently in flight — cleared
	// and replaced on every new trigger so a rapid re-toggle never stacks two scheduled plays.
	const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [visible, setVisible] = useState(false);
	// Bumped on every settled play to force a fresh `<Lottie>`/wrapper mount — restarts both the
	// animation and the fade-in keyframes.
	const [playKey, setPlayKey] = useState(0);

	useEffect(() => {
		const wasActive = prevActiveRef.current;
		prevActiveRef.current = active;
		if (wasActive === active || reducedMotion) return;
		directionRef.current = active ? 1 : -1;

		// Cancel any still-pending previous trigger's scheduled mount, and unmount whatever's currently
		// playing right away (rather than letting it keep animating toward its own old boundary) — the
		// settle delay below then guarantees the next instance never mounts on top of the previous
		// one's still-in-flight teardown.
		if (settleTimeoutRef.current != null) clearTimeout(settleTimeoutRef.current);
		setVisible(false);

		const timeoutId = setTimeout(() => {
			settleTimeoutRef.current = null;
			setVisible(true);
			setPlayKey((key) => key + 1);
		}, REPLAY_SETTLE_MS);
		settleTimeoutRef.current = timeoutId;

		// Runs before the next dependency change AND on unmount — covers both a `reducedMotion`-only
		// flip (which the guard clause above returns early from, without reaching the clearTimeout at
		// the top of this run) and the component unmounting mid-delay. Guards against clearing a
		// timeout that already fired and was replaced by a newer one (settleTimeoutRef.current would
		// then be null or a different id).
		return () => {
			if (settleTimeoutRef.current === timeoutId) {
				clearTimeout(timeoutId);
				settleTimeoutRef.current = null;
			}
		};
	}, [active, reducedMotion]);

	// Autoplay is off (below) so playback direction can be set BEFORE the first frame renders — this
	// fires once the fresh instance is ready to be driven (lottie-react v3's `subscriptions.ready`,
	// replacing v2's `onDOMLoaded`), and kicks off the play manually in the direction this trigger
	// asked for. `seek({ percent })` (v3) replaces v2's `goToAndPlay(frame, true)` — the percent unit
	// means this never needs the animation's own frame count (v3's `LottieHandle` doesn't expose one;
	// only the full `LottieInstance` does, via a hook this component has no reason to use).
	const handleReady = () => {
		const instance = lottieRef.current;
		if (!instance) return;
		if (directionRef.current === -1) {
			instance.setDirection("reverse");
			instance.seek({ percent: 100 });
		} else {
			instance.setDirection("forward");
			instance.seek({ percent: 0 });
		}
		instance.play();
	};

	if (!visible) return null;

	return (
		<Box
			key={playKey}
			aria-hidden="true"
			data-testid={dataTestId}
			sx={[
				ENTRANCE_SX,
				{
					position: "absolute",
					bottom: "100%",
					left: "50%",
					transform: "translateX(-50%)",
					pointerEvents: "none",
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Lottie
				lottieRef={lottieRef}
				src={animationData}
				loop={false}
				autoplay={false}
				subscriptions={{ ready: handleReady, complete: () => setVisible(false) }}
				style={{ width: size, height: size }}
			/>
		</Box>
	);
};
